import { Grid, Tab, Tabs } from '@mui/material';

import { UserContext, UserContextPayload } from '../../context/userContext';
import { useContext, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import styles from '../../assets/styles/componentStyles/billingStyles/billing.module.scss';

import { useGenericStore } from '../../store/genericStore';
import { useBillingStore } from '../../store/billingStore';

import CurrentPlanCard from './billingUsageComponents/currentPlanCard';
import UsageNextBillingCard from './billingUsageComponents/usageNextBillingCard';
import PlanSummaryCard from './billingPlansComponents/planSummaryCard';
import BillingSection from './billingUsageComponents/billingSection';
import UsageSection from './billingUsageComponents/usageSection';

enum BillingUsageTab {
    Billing = "billing",
    Usage = "usage",
}

const BillingUsage = () => {

    const { setFlagLoading } = useContext(UserContext) as UserContextPayload;

    const [ selectedTab, setSelectedTab ] = useState<BillingUsageTab>(BillingUsageTab.Billing);

    const { showError } = useGenericStore(useShallow((state) => ({
        showError: state.showError,
    })));

    const { billingSubscriptionDetails, isBillingSubscriptionLoading, getBillingSubscriptionDetailsApi, transactionStatuses, getTransactionStatusesApi } = useBillingStore(useShallow((state) => ({
        billingSubscriptionDetails: state.billingSubscriptionDetails,
        isBillingSubscriptionLoading: state.isBillingSubscriptionLoading,
        getBillingSubscriptionDetailsApi: state.getBillingSubscriptionDetailsApi,
        transactionStatuses: state.transactionStatuses,
        getTransactionStatusesApi: state.getTransactionStatusesApi,
    })));

    const upcomingPlan = billingSubscriptionDetails?.upcoming_plan;
    const hasNextBillingDate = !!billingSubscriptionDetails?.current_plan?.next_charge_at;
    const showSideCard = !!upcomingPlan || hasNextBillingDate;

    const fetchBillingSubscriptionDetails = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        
        const res = await getBillingSubscriptionDetailsApi();
        
        if (!res?.status) {
            showError(res?.error?.message || "Something went wrong");
        }
        
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };

    const fetchTransactionStatuses = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        
        const res = await getTransactionStatusesApi();
        
        if (!res?.status) {
            showError(res?.error?.message || "Something went wrong");
        }
        
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };

    useEffect(() => {
        if(!billingSubscriptionDetails) {
            fetchBillingSubscriptionDetails();
        }
        if(!transactionStatuses) {
            fetchTransactionStatuses();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isBillingSubscriptionLoading) {
        return <></>;
    }

    return (
        <Grid container spacing={3} className={styles.billingUsageSection}>
            
            <Grid item xs={12} lg={6} className={styles.planCardItem}>
                <CurrentPlanCard />
            </Grid>

            {showSideCard && (
                <Grid item xs={12} lg={6} className={styles.planCardItem}>
                    {upcomingPlan ? (
                        <PlanSummaryCard
                            plan={upcomingPlan}
                            title="Upcoming Plan"
                            addOnsLabel="Upcoming Add-ons"
                            showEffectiveFromDate
                        />
                    ) : (
                        <UsageNextBillingCard />
                    )}
                </Grid>
            )}

            <Grid item xs={12}>
                <Tabs
                    value={selectedTab}
                    onChange={(_, newValue: BillingUsageTab) => setSelectedTab(newValue)}
                    aria-label="billing usage tabs"
                    className={styles.billingUsageTabs}
                    TabIndicatorProps={{ style: { display: "none" } }}
                >
                    <Tab
                        label="Billing"
                        value={BillingUsageTab.Billing}
                        className={styles.billingUsageTabItem}
                        disableRipple
                    />
                    <Tab
                        label="Usage"
                        value={BillingUsageTab.Usage}
                        className={styles.billingUsageTabItem}
                        disableRipple
                    />
                </Tabs>
            </Grid>

            <Grid item xs={12}>
                {selectedTab === BillingUsageTab.Billing ? (
                    <BillingSection />
                ) : (
                    <UsageSection />
                )}
            </Grid>

        </Grid>
    )
}

export default BillingUsage;
